import { MapCreationOptions } from '@msaf/maps-common'
import { MapMenuConfig, MapPage } from '@msaf/maps-react'
import { useLocation, useNavigate } from '@msaf/router-react'
import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ViewFeaturesPanel } from '../view-features-panel'
import { DEFAULT_CENTER, DEFAULT_FEATURE_ZOOM_LEVEL, linzBasemap } from '../../../config/map'
import { RouteMode, RouteParams } from '../../../types/route'
import { AddFeaturePanel } from '../add-feature-panel'
import { FeatureMapContext } from '../context'
import { ContextPanel } from '../context-panel'
import { EditFeaturesPanel } from '../edit-features-panel'
import { FeatureMapFeatureOptions } from '../types'
import { MAP_CONFIG_BASE } from './constants'
import { useRegisterFeatureFilterGroups } from '../feature-filter-groups/use-register-feature-filter-groups'
import L from 'leaflet'
import { useMapLayers } from '../../../hooks/use-map-layers'
import { LoadingPage } from '../../loading-page'
import { AddressLookupPanel } from '../../address-lookup-panel'
import { usePassedState } from '@msaf/core-react'

export type PageMapOptions = Pick<MapCreationOptions, 'center' | 'zoom'>

export function Page({
  setMap: setMapExternal,
  onSave,
  mapOptions,
  featureToggle,
  defaultMenuIsOpen = true,
  isLoading = false,
}: {
  setMap: (map: L.Map | undefined) => void
  onSave?: () => void
  mapOptions?: PageMapOptions
  featureToggle: FeatureMapFeatureOptions
  defaultMenuIsOpen?: boolean
  isLoading?: boolean
}) {
  const { state: map, setState: setMap } = usePassedState<L.Map | undefined>(undefined, setMapExternal)
  const navigate = useNavigate()
  const location = useLocation()
  const { mode, id } = useParams<RouteParams>()
  const originUrl = useMemo(() => {
    // If no origin, navigate to the pre-checks route.
    return new URLSearchParams(location.search).get('origin') ?? `/plans/${mode}/${id}/plan/pre-checks`
  }, [location])

  const [isMenuPanelOpen, setIsMenuPanelOpen] = useState(defaultMenuIsOpen)
  const { selectedFeature, clearFeatureSelection, filterGroups } = useContext(FeatureMapContext)

  const { layers, defaultLayers, isLoading: isLoadingLayerConfig } = useMapLayers({ planId: id })

  const linzBaseMap = linzBasemap()
  const baseMaps = L.layerGroup([linzBaseMap])

  useRegisterFeatureFilterGroups()

  const menus = useMemo(() => {
    const menuConfig: MapMenuConfig[] = []
    if (featureToggle.create) {
      menuConfig.push({
        menuIcon: 'marquee',
        menuName: 'Add actions',
        menuKey: 'add-actions',
        menuComponent: <AddFeaturePanel setIsMenuPanelOpen={setIsMenuPanelOpen} />,
      })
    }

    const filterCount = filterGroups?.filter((group) => group.filterFunction).length

    if (featureToggle.edit || featureToggle.delete) {
      menuConfig.push({
        menuIcon: 'layers',
        menuName: 'Edit actions',
        menuKey: 'edit-actions',
        badgeCount: filterCount,
        menuComponent: <EditFeaturesPanel setIsMenuPanelOpen={setIsMenuPanelOpen} />,
      })
    } else if (featureToggle.view !== false) {
      menuConfig.push({
        menuIcon: 'layers',
        menuName: 'View actions',
        menuKey: 'view-actions',
        badgeCount: filterCount,
        menuComponent: <ViewFeaturesPanel setIsMenuPanelOpen={setIsMenuPanelOpen} />,
      })
    }

    menuConfig.push({
      menuName: 'Location search',
      menuKey: 'location',
      menuIcon: 'location',
      menuComponent: <AddressLookupPanel map={map} />,
    })

    return menuConfig
  }, [filterGroups, map])

  if (isLoadingLayerConfig) {
    return <LoadingPage />
  }

  return (
    <MapPage
      appLogo={MAP_CONFIG_BASE.appLogo}
      backLinkConfig={{
        label: 'Back',
        onClick: () => navigate(originUrl),
      }}
      headerActionsConfig={
        mode === RouteMode.EDIT && onSave
          ? [
              {
                key: 'saveBtn',
                label: 'Save',
                onClick: onSave,
                isDisabled: isLoading,
              },
            ]
          : []
      }
      setMap={(map) => {
        map?.addControl(L.control.layers({}, layers, { position: 'bottomright', autoZIndex: false }))
        map?.addLayer(linzBaseMap)
        defaultLayers.forEach((layer) => {
          map?.addLayer(layer)
        })
        map && L.control.scale({ position: 'bottomright', imperial: false }).addTo(map)

        setMap(map)
      }}
      mapOptions={{
        center: mapOptions?.center ?? DEFAULT_CENTER,
        editable: true,
        baseLayers: baseMaps,
        zoom: mapOptions?.zoom ?? DEFAULT_FEATURE_ZOOM_LEVEL,
        zoomControl: true,
        // Increase the click tolerance to ensure that clicking on fencing is possible.
        renderer: new L.Canvas({ tolerance: 2 }),
      }}
      menuConfig={menus}
      contextPanel={ContextPanel}
      isMenuPanelOpen={isMenuPanelOpen}
      setIsMenuPanelOpen={setIsMenuPanelOpen}
      isContextPanelOpen={selectedFeature != null}
      setIsContextPanelOpen={() => clearFeatureSelection?.()}
      showMapPageHeader
    />
  )
}
